/**
 * The discussions writer on a phone (Community mobile §4, design M1c, ≤767.98px): the words and
 * small decisions of the full-screen writer — the top bar's draft status, why Post is off, the
 * compact checklist, the category / link-gear rows and their sheet, the toolbar's keyboard
 * offset. Every posting and validation rule stays `discussions-writer.ts`'s; nothing here changes
 * what is sent or stored.
 */
import { categoryChildren } from './model'
import {
  TITLE_MIN_TO_POST,
  linkPreview,
  postBlocker,
  topCategories,
  writerCategoryRows,
  type ChecklistItem,
  type DraftBlock,
  type DraftGear,
  type WriterDraft,
} from './discussions-writer'
import type { CommunityCategoryNode } from './types'

/** The phone layout's query — the same breakpoint as every community phone rule. */
export const WRITER_PHONE_QUERY = '(max-width: 767.98px)'

// ── the top bar ──────────────────────────────────────────────────────────────

export interface PhoneStatusInput {
  /** Editing a posted discussion: no local draft, nothing autosaves. */
  edit: boolean
  /** The stored draft has been read (the effect after mount). */
  loaded: boolean
  /** A change autosave has not written yet. */
  dirty: boolean
  saveState: 'idle' | 'saved' | 'failed'
  posting: boolean
  /** A stored draft was loaded on arrival (it is saved — it came from storage). */
  restored: boolean
  /** Editing: what would be sent is empty. */
  unchanged: boolean
}

/**
 * The top bar's middle line. A new post autosaves (1.5 s after a change), so it reads "Saving…"
 * while a change waits and "Draft saved on this device" once written (a tap says where to find it:
 * Writer's where-line); a browser that blocks storage says so.
 * The edit view keeps no draft (Writer: `localDraft`), so it says whether there is anything to
 * save instead. Browser state only: the server pass (loaded false) is always "".
 */
export function phoneDraftStatus(s: PhoneStatusInput): string {
  if (s.posting) {
    return s.edit ? 'Saving…' : 'Posting…'
  }
  if (!s.loaded) {
    return ''
  }
  if (s.edit) {
    return s.unchanged ? 'No changes yet' : 'Unsaved changes'
  }
  if (s.saveState === 'failed') {
    return "Drafts can't be saved here"
  }
  if (s.dirty) {
    return 'Saving…'
  }
  return s.saveState === 'saved' || s.restored ? 'Draft saved on this device' : ''
}

export interface PostStateInput {
  pending: boolean
  posting: boolean
  /** Editing and nothing changed. */
  unchanged: boolean
}

/**
 * Why Post is off, short enough for the toolbar's right side — or null when Post is on (or
 * busy posting). The rule is `canPost` / `postBlocker` (title ≥ 9 characters; a discussion needs
 * text) plus the writer's own gates (uploads, an unchanged edit); this only words it.
 */
export function postDisabledReason(draft: WriterDraft, s: PostStateInput): string | null {
  if (s.posting) {
    return null
  }
  const title = draft.title.trim()
  if (!title) {
    return 'Add a title to post'
  }
  if (title.length < TITLE_MIN_TO_POST) {
    return `Title needs ${TITLE_MIN_TO_POST}+ characters`
  }
  if (postBlocker(draft)) {
    return 'Add some text to post'
  }
  if (s.pending) {
    return 'Photos are uploading'
  }
  if (s.unchanged) {
    return 'No changes to save'
  }
  return null
}

export interface CloseContext {
  /** The page the document first loaded (`__NEXT_DATA__.page`), and the page shown now
   *  (`router.pathname`): different → the writer was reached by a client-side navigation. */
  initialPage: string | null
  currentPage: string | null
  /** `document.referrer`, `location.origin`, `history.length`. */
  referrer: string
  origin: string
  historyLength: number
}

/**
 * ✕ goes Back when the page before the writer is a page of this site — reached in the app (a
 * client-side navigation; Next keeps no history index in this version), or by a full load from
 * one of its own links with a history entry to return to. Else (a typed URL, a new tab, another
 * site) it goes to the Discussions list instead of off the site.
 */
export function closeGoesBack(c: CloseContext): boolean {
  if (c.initialPage && c.currentPage && c.initialPage !== c.currentPage) {
    return true
  }
  if (c.historyLength < 2 || !c.referrer) {
    return false
  }
  try {
    return new URL(c.referrer).origin === c.origin
  } catch {
    return false
  }
}

// ── the compact checklist ────────────────────────────────────────────────────

/** "1 of 4": the items ticked, of all. */
export function checklistCount(items: readonly ChecklistItem[]): string {
  return `${items.filter(i => i.done).length} of ${items.length}`
}

/**
 * The compact card's rows: every ticked item and the first one not yet ticked, in the list's
 * order — or every item once the reader opened the card. `hidden` is how many are left out.
 */
export function visibleChecklistItems(
  items: readonly ChecklistItem[],
  expanded: boolean
): { items: ChecklistItem[]; hidden: number } {
  if (expanded) {
    return { items: [...items], hidden: 0 }
  }
  const next = items.findIndex(i => !i.done)
  const shown = items.filter((item, i) => item.done || i === next)
  return { items: shown, hidden: items.length - shown.length }
}

// ── the category and link-gear rows ──────────────────────────────────────────

/** A row of the category sheet: a top-level category (depth 0) or one of its children (1). */
export interface CategorySheetRow {
  node: CommunityCategoryNode
  depth: 0 | 1
}

/** The category sheet's one list: each top-level category, its children indented under it —
 *  the same nodes the desktop pills offer (`topCategories` + `categoryChildren`). */
export function categorySheetRows(
  categories: readonly CommunityCategoryNode[]
): CategorySheetRow[] {
  const rows: CategorySheetRow[] = []
  for (const top of topCategories(categories)) {
    rows.push({ node: top, depth: 0 })
    for (const child of categoryChildren([...categories], top.full_slug)) {
      rows.push({ node: child, depth: 1 })
    }
  }
  return rows
}

/** The sheet row that shows as picked, and the category row's value ("Earbuds › Wireless earbuds"
 *  for a child) — from `writerCategoryRows`, so a deeper `?category=` preset lights the child
 *  above it, as the desktop pills do. Both null while nothing is picked ("Choose one"). */
export function pickedCategory(
  categories: readonly CommunityCategoryNode[],
  selectedId: string | null
): { rowId: string | null; label: string | null } {
  const rows = writerCategoryRows(categories, selectedId)
  const parent = rows.top.find(t => t.id === rows.parentId) ?? null
  if (!parent) {
    return { rowId: null, label: null }
  }
  const child = rows.children.find(c => c.id === rows.childId) ?? null
  return child
    ? { rowId: child.id, label: `${parent.name} › ${child.name}` }
    : { rowId: parent.id, label: parent.name }
}

/** The link-gear row's value: the linked names, or null while nothing is linked (the row keeps
 *  "Owners get notified"). */
export function gearRowValue(gear: readonly DraftGear[]): string | null {
  return gear.length ? gear.map(g => g.name).join(', ') : null
}

// ── the toolbar over the keyboard ────────────────────────────────────────────

/**
 * How far above the layout viewport's bottom the visible area ends — the on-screen keyboard's
 * height on iOS / Chrome (the layout viewport does not shrink; the visual one does). 0 without
 * `visualViewport` or with no keyboard; never negative (an overscroll bounce).
 */
export function keyboardInset(
  innerHeight: number,
  visualHeight: number | null,
  visualOffsetTop: number | null
): number {
  if (visualHeight === null) {
    return 0
  }
  return Math.max(0, Math.round(innerHeight - visualHeight - (visualOffsetTop ?? 0)))
}

// ── reorder mode ─────────────────────────────────────────────────────────────

/** A block as one line in reorder mode: what it is, and enough of it to tell two apart. */
export function blockSummary(block: DraftBlock): { kind: string; text: string } {
  const clip = (s: string): string => {
    const one = s.replace(/\s+/g, ' ').trim()
    return one.length > 60 ? `${one.slice(0, 59)}…` : one
  }
  switch (block.type) {
    case 'text':
      return { kind: 'Text', text: clip(block.body) || 'Empty' }
    case 'heading':
      return { kind: 'Heading', text: clip(block.text) || 'Empty' }
    case 'images': {
      const n = block.images.length
      return { kind: 'Photos', text: n ? `${n} ${n === 1 ? 'photo' : 'photos'}` : 'No photos yet' }
    }
    case 'link':
      return { kind: 'Link', text: linkPreview(block.url)?.domain ?? (clip(block.url) || 'Empty') }
  }
  return { kind: '', text: '' }
}

/**
 * Where a dragged row lands: the number of OTHER rows whose middle is above the pointer. `mids`
 * are the rows' vertical middles in their current order, `from` the dragged row's index.
 */
export function dropIndex(mids: readonly number[], y: number, from: number): number {
  return mids.filter((mid, i) => i !== from && mid < y).length
}

// ── never lose the last words ────────────────────────────────────────────────

/**
 * Whether leaving the page's foreground must write the draft now instead of waiting for the
 * 1.5 s autosave: the tab went hidden (`visibilitychange`) or is being put away (`pagehide` —
 * iOS may discard a backgrounded tab without another event). Only a new post's local draft
 * (the edit view never autosaves), only with a change not yet stored, never after posting.
 */
export function flushDraftOnHide(s: {
  event: 'visibilitychange' | 'pagehide'
  visibilityState: string
  localDraft: boolean
  loaded: boolean
  dirty: boolean
  posted: boolean
}): boolean {
  if (!s.localDraft || !s.loaded || !s.dirty || s.posted) {
    return false
  }
  return s.event === 'pagehide' || s.visibilityState === 'hidden'
}

/**
 * How far to scroll so the line being typed clears the toolbar: the field's bottom below the
 * bar's top (plus a small gap), while the caret is at the field's end — the usual case when
 * writing; 0 otherwise (the reader is editing higher up, the browser keeps that line in view).
 */
export function caretUnderBar(s: {
  fieldBottom: number
  barTop: number
  caretAtEnd: boolean
}): number {
  if (!s.caretAtEnd) {
    return 0
  }
  const over = s.fieldBottom - s.barTop
  return over > 0 ? Math.ceil(over + 8) : 0
}

/** The radio-group keys (WAI-ARIA radio pattern): the row index an arrow / Home / End moves to,
 *  wrapping at both ends; null for any other key. */
export function radioKeyStep(key: string, index: number, count: number): number | null {
  if (count <= 0) {
    return null
  }
  switch (key) {
    case 'ArrowDown':
    case 'ArrowRight':
      return (index + 1) % count
    case 'ArrowUp':
    case 'ArrowLeft':
      return (index - 1 + count) % count
    case 'Home':
      return 0
    case 'End':
      return count - 1
    default:
      return null
  }
}
